import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormGroup, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, finalize } from 'rxjs';

import {
  ImportColumnRole,
  ImportedQuestion,
  ImportedRow,
  TestImportCommit,
  TestImportPreview,
  TestImportTargetPreview,
  TestImportResult,
} from '../../../../core/interfaces/test-import';
import { LearningPath } from '../../../../core/interfaces/learning-path';
import { Professor } from '../../../../core/interfaces/professor';
import { Subject } from '../../../../core/interfaces/subject';
import { LearningService } from '../../../../core/services/learning-path.service';
import { ProfessorService } from '../../../../core/services/professor.service';
import { TestImportService } from '../../../../core/services/tests/test-import.service';
import {
  UploadPurpose,
  UploadService,
} from '../../../../core/services/uploads/upload.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { SwalService } from '../../../../core/services/utils/swal.service';
import { TestInfoFormComponent } from '../../../ui/views/test-info-form/test-info-form.component';
import { MathTextComponent } from '../../../../shared/math/math-text.component';
import { RichFieldComponent } from '../../../../shared/math/rich-field/rich-field.component';

/** Étapes du parcours, dans l'ordre. */
type Step = 'source' | 'mapping' | 'review' | 'done';

/** Un rôle proposé dans le sélecteur de colonne. */
interface RoleOption {
  value: ImportColumnRole;
  labelKey: string;
}

/**
 * Import d'un référentiel diagnostique vers la banque de questions.
 *
 * Le parcours est délibérément en quatre temps. L'extraction d'un PDF reste
 * approximative — le surlignage y est déduit de la mise en page et l'arabe
 * restitué dans l'ordre de dessin — donc rien n'est écrit avant que
 * l'utilisateur ait vu le tableau, choisi le rôle de chaque colonne, puis relu
 * les questions que ce mapping produit.
 */
/**
 * Vrai pour une question que le test posera réellement.
 *
 * <p>Le document fait foi : chaque ligne exploitable devient une question, même
 * si elle en répète une autre ou si la banque la connaît déjà. Les ressemblances
 * sont signalées à côté, elles ne retirent rien du compte — l'import ne les
 * tranche pas, et ce qu'on refuse est ailleurs : réimporter le test entier dans
 * une matière qui l'a déjà.</p>
 */
function isRetained(q: ImportedQuestion): boolean {
  return q.answers.length >= 2;
}

/**
 * Largeurs que la base impose vraiment — `question_bank.title` est un
 * `varchar(4000)` — formules LaTeX comprises —, `themes.title` un `varchar(255)`.
 *
 * <p>L'API refuse au-delà ; les borner ici évite de le découvrir après avoir
 * saisi trois paragraphes. Les autres champs de référentiel tombent dans du
 * `jsonb` ou du `text` : leur poser une limite ronde tronquerait une liste de
 * prérequis légitime au nom d'une contrainte qui n'existe pas.</p>
 */
const TITLE_MAX = 4000;
const THEME_MAX = 255;

/** Un champ de référentiel corrigible à l'écran. */
interface DraftFieldDef {
  readonly name: 'theme' | 'level' | 'skill' | 'targeted' | 'prerequisites';
  readonly labelKey: string;
  readonly max?: number;
}

/**
 * Les cinq champs que le mapping remplit, dans l'ordre où l'écran les montre.
 *
 * <p>Ils voyagent en clair jusqu'au serveur, qui les <i>résout</i> : le thème
 * devient une leçon, la compétence un intitulé sous un domaine, le niveau une
 * classe. Corriger ici, c'est corriger ce que l'import va ranger.</p>
 */
const DRAFT_FIELDS: readonly DraftFieldDef[] = [
  { name: 'theme', labelKey: 'TEST_IMPORT.ROLE.THEME', max: THEME_MAX },
  { name: 'level', labelKey: 'TEST_IMPORT.ROLE.LEVEL' },
  { name: 'skill', labelKey: 'TEST_IMPORT.ROLE.SKILL' },
  { name: 'targeted', labelKey: 'TEST_IMPORT.ROLE.TARGETED' },
  { name: 'prerequisites', labelKey: 'TEST_IMPORT.ROLE.PREREQUISITES' },
];

/** Une valeur de référentiel saisie : vidée, c'est « pas de valeur ». */
function trimmedOrNull(value: string | null | undefined): string | null {
  const v = (value ?? '').trim();
  return v.length > 0 ? v : null;
}

/**
 * Le contenu qui décide de l'empreinte : l'énoncé et le texte des options.
 *
 * <p>Ni l'ordre des champs de référentiel, ni la bonne réponse n'en font
 * partie — le serveur calcule l'empreinte sur ces deux-là seulement. C'est ce
 * qui permet de dire si une correction a changé l'identité de la question.</p>
 */
function sameContent(a: ImportedQuestion, b: ImportedQuestion): boolean {
  return (
    a.title === b.title &&
    a.answers.length === b.answers.length &&
    a.answers.every((x, i) => x.content === b.answers[i].content)
  );
}

/**
 * Un parcours supplémentaire, la matière qui l'y reçoit, et le professeur qui
 * y sera titulaire du test.
 *
 * <p>Le titulaire est propre à chaque parcours : un test appartient au
 * professeur qui l'enseignera. Sans lui, la matière reçoit ses questions mais
 * aucun test n'y est composé — l'écran le dit plutôt que de le laisser
 * découvrir au rapport.</p>
 */
interface ImportTarget {
  pathId: number;
  pathTitle: string;
  subjectId: number;
  subjectName: string;
  professorId: number | null;
  professorName: string | null;

  /**
   * Cycle du parcours, conservé pour signaler les cibles qui ne relèvent pas
   * de celui de la première retenue.
   *
   * <p>Le verdict était figé à l'ajout. Retirer la première cible changeait le
   * cycle de référence, et les autres gardaient une alerte — ou son absence —
   * calculée sur un parcours qui n'était plus là.</p>
   */
  pathLevel: LearningPath['level'] | null;
}

@Component({
  selector: 'app-test-import',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, TestInfoFormComponent, MathTextComponent, RichFieldComponent],
  templateUrl: './test-import.component.html',
  styleUrl: './test-import.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TestImportComponent {
  private readonly uploads = inject(UploadService);
  private readonly imports = inject(TestImportService);
  private readonly learning = inject(LearningService);
  private readonly professorsApi = inject(ProfessorService);
  private readonly auth = inject(AuthService);
  private readonly swal = inject(SwalService);
  private readonly translate = inject(TranslateService);
  private readonly router = inject(Router);

  readonly step = signal<Step>('source');

  /**
   * L'administrateur importe pour le compte d'un professeur : son jeton ne
   * porte aucun identifiant de professeur, il doit donc désigner le titulaire
   * des questions. Un professeur, lui, importe pour lui-même et ne voit pas ce
   * choix.
   */
  readonly isAdmin = this.auth.isAdmin();

  /* ── Étape 1 : la source ──────────────────────────────────────── */

  readonly file = signal<File | null>(null);
  readonly learningPaths = signal<LearningPath[]>([]);

  /**
   * Les parcours qui recevront le référentiel — tous, sans exception.
   *
   * <p>Un même programme s'enseigne dans plusieurs parcours. L'écran demandait
   * le couple parcours/matière en tête, les parcours « supplémentaires » plus
   * bas, et le professeur ailleurs encore : trois endroits pour un même geste,
   * et le premier parcours n'avait pas de titulaire propre. Tout passe
   * désormais par le seul bouton d'ajout.</p>
   *
   * <p>Le couple parcours/matière se choisit à la main, comme on affecte une
   * matière à un professeur. Un appariement deviné sur le nom aurait été plus
   * court à utiliser, mais il ne propose rien quand aucun parcours ne
   * correspond — et l'écran paraît alors dépourvu de la fonctionnalité.</p>
   */
  readonly chosenTargets = signal<ImportTarget[]>([]);

  /**
   * La première cible retenue tient lieu de dépôt : c'est sa matière que porte
   * la requête d'analyse, et son titulaire qui vaut par défaut.
   *
   * <p>Le serveur, lui, les traite toutes pareillement — il dédoublonne les
   * cibles et compose un test par matière, chacune avec le sien.</p>
   */
  readonly depositTarget = computed(() => this.chosenTargets()[0] ?? null);

  readonly learningPathId = computed(() => this.depositTarget()?.pathId ?? null);
  readonly subjectId = computed(() => this.depositTarget()?.subjectId ?? null);
  readonly professorId = computed(() => this.depositTarget()?.professorId ?? null);

  /** Couple en cours de saisie, au-dessus du bouton d'ajout. */
  readonly targetPathId = signal<number | null>(null);
  readonly targetSubjectId = signal<number | null>(null);
  readonly targetSubjects = signal<Subject[]>([]);

  /** Professeurs affectés à la matière en cours de saisie. Chargé pour l'admin seul. */
  readonly targetProfessors = signal<Professor[]>([]);
  readonly targetProfessorId = signal<number | null>(null);
  readonly language = signal<'ar' | 'fr' | 'en'>('ar');
  readonly direction = signal<'rtl' | 'ltr'>('rtl');

  readonly uploadPercent = signal<number | null>(null);
  readonly analyzing = signal(false);

  /* ── Étape 2 : le mapping ─────────────────────────────────────── */

  readonly preview = signal<TestImportPreview | null>(null);

  /** Rôle retenu par colonne. Part de la proposition du serveur. */
  readonly mapping = signal<Record<number, ImportColumnRole>>({});

  readonly roleOptions: RoleOption[] = [
    { value: ImportColumnRole.Ignore, labelKey: 'TEST_IMPORT.ROLE.IGNORE' },
    { value: ImportColumnRole.Question, labelKey: 'TEST_IMPORT.ROLE.QUESTION' },
    { value: ImportColumnRole.Theme, labelKey: 'TEST_IMPORT.ROLE.THEME' },
    { value: ImportColumnRole.Level, labelKey: 'TEST_IMPORT.ROLE.LEVEL' },
    { value: ImportColumnRole.Skill, labelKey: 'TEST_IMPORT.ROLE.SKILL' },
    { value: ImportColumnRole.Prerequisites, labelKey: 'TEST_IMPORT.ROLE.PREREQUISITES' },
    { value: ImportColumnRole.Targeted, labelKey: 'TEST_IMPORT.ROLE.TARGETED' },
    { value: ImportColumnRole.Tags, labelKey: 'TEST_IMPORT.ROLE.TAGS' },
  ];

  /** Colonnes du tableau, dans l'ordre d'affichage. */
  readonly columns = computed(() => {
    const p = this.preview();
    if (!p) return [];
    return Array.from({ length: p.columnCount }, (_, i) => i);
  });

  /** Les premières lignes suffisent à juger du mapping. */
  readonly sampleRows = computed(() => this.preview()?.rows.slice(0, 5) ?? []);

  readonly hasQuestionColumn = computed(() =>
    Object.values(this.mapping()).includes(ImportColumnRole.Question),
  );

  /**
   * Le thème est aussi indispensable que la question : l'API rattache chaque
   * question importée à un thème, et écarte celles qui n'en ont pas. Sans
   * cette colonne, l'import aboutit — et ne crée rien. Mieux vaut le dire ici
   * que laisser l'utilisateur découvrir soixante-six lignes ignorées au bilan.
   */
  readonly hasThemeColumn = computed(() =>
    Object.values(this.mapping()).includes(ImportColumnRole.Theme),
  );

  /** Question et thème sont requis ; le reste est facultatif. */
  readonly mappingValid = computed(
    () => this.hasQuestionColumn() && this.hasThemeColumn(),
  );

  /* ── Étape 3 : la relecture ───────────────────────────────────── */

  readonly questions = signal<ImportedQuestion[]>([]);

  /**
   * Ce que chaque matière visée recevrait.
   *
   * <p>Les questions y sont identiques — c'est le même fichier — mais leur
   * statut de doublon ne l'est pas : une question peut exister dans un
   * parcours et pas dans l'autre. La relecture s'appuie sur la première,
   * celle de la matière de dépôt ; les autres servent à dire, par test, ce
   * qui y sera réellement créé.</p>
   */
  readonly targetPreviews = signal<TestImportTargetPreview[]>([]);
  readonly projecting = signal(false);
  readonly committing = signal(false);

  /* ── Édition d'une question ───────────────────────────────────── */

  /** Ligne en cours d'édition, s'il y en a une. Une seule à la fois. */
  readonly editingRow = signal<number | null>(null);

  /**
   * Copie de travail de la question éditée.
   *
   * <p>L'original n'est remplacé qu'à la validation. Sans cette copie,
   * « Annuler » rendrait la question telle que la dernière frappe l'a laissée
   * — c'est-à-dire pas du tout ce que le bouton annonce.</p>
   */
  readonly draft = signal<ImportedQuestion | null>(null);

  /** Lignes que l'utilisateur a modifiées, pour les signaler dans la liste. */
  readonly editedRows = signal<ReadonlySet<number>>(new Set<number>());

  /** Un énoncé vide ne désigne plus rien : c'est le seul refus de l'éditeur. */
  readonly draftValid = computed(() => !!this.draft()?.title.trim());

  /* ── Réglages du test composé ─────────────────────────────────── */

  /**
   * Le test importé se paramètre avec le formulaire de la création manuelle,
   * `app-test-info-form`, et non avec des champs recopiés ici : deux jeux de
   * champs auraient fini par diverger, et un test composé à l'import n'a
   * aucune raison d'avoir moins de réglages qu'un test saisi à la main.
   *
   * <p>Le titre y garde sa règle propre : laissé vide, les questions rejoignent
   * la banque sans être assemblées.</p>
   */
  private testForm: FormGroup | null = null;
  private formWatch?: Subscription;

  /**
   * Dernières valeurs saisies. L'aller-retour vers l'étape du mapping détruit
   * le formulaire ; sans cette mémoire, le titre et les réglages seraient à
   * ressaisir à chaque retour.
   */
  readonly testInfo = signal<Record<string, unknown> | null>(null);

  /** Titre courant, pour l'affichage du rappel « un test sera composé ». */
  readonly composesTest = computed(
    () => String(this.testInfo()?.['title'] ?? '').trim().length > 0,
  );

  /**
   * Durée proposée : une minute et demie par question importée, arrondie au
   * multiple de 5 et bornée à [10, 180]. Une valeur fixe serait fausse dans
   * les deux sens — un référentiel de 120 questions ne se passe pas en une
   * heure, et un extrait de 8 questions n'en demande pas une non plus.
   */
  readonly suggestedMinutes = computed(() => {
    // Les doublons comptent : ils ne sont pas recréés, mais ils sont dans le
    // test. Ne compter que les créations proposait une heure pour un test de
    // cinquante-deux questions dont aucune n'était neuve.
    const count = this.retained().length;
    if (count === 0) return 60;
    const raw = Math.ceil((count * 1.5) / 5) * 5;
    return Math.min(180, Math.max(10, raw));
  });

  /** Valeurs d'ouverture du formulaire : celles déjà saisies, sinon la proposition. */
  readonly testFormSeed = computed(
    () => this.testInfo() ?? { timeLimitMinutes: this.suggestedMinutes() },
  );

  readonly importable = computed(() =>
    this.questions().filter((q) => !q.duplicateOfQuestionId && q.answers.length >= 2),
  );
  readonly duplicates = computed(() =>
    this.questions().filter((q) => !!q.duplicateOfQuestionId),
  );

  /**
   * Ce que le test posera réellement : le neuf exploitable, et l'existant.
   *
   * <p>Une question déjà en banque n'est pas recréée, mais c'est bien elle que
   * le fichier désigne : elle entre dans le test. La compter comme écartée
   * annonçait « 0 question » là où le test en posait cinquante-deux.</p>
   */
  readonly retained = computed(() => this.questions().filter(isRetained));
  readonly withoutCorrect = computed(() =>
    this.questions().filter(
      (q) => q.answers.length > 0 && !q.answers.some((a) => a.isCorrect),
    ),
  );

  /* ── Étape 4 : le bilan ───────────────────────────────────────── */

  readonly result = signal<TestImportResult | null>(null);

  /**
   * Un test composé doit avoir un titulaire : il apparaîtra dans la liste d'un
   * professeur, qui l'ouvrira et le validera. Sans lui, la matière reçoit ses
   * questions mais aucun test n'y est composé — autant l'annoncer ici plutôt
   * que de le laisser découvrir au bilan.
   *
   * <p>Porte sur la première cible : c'est son titulaire que la requête
   * transporte. Les suivantes signalent le leur sur leur propre ligne.</p>
   */
  readonly needsProfessor = computed(
    () => this.isAdmin && this.composesTest() && this.professorId() === null,
  );

  /**
   * Le dépôt est possible dès qu'une question atteindra sa destination — qu'elle
   * soit créée ou reprise de la banque.
   *
   * <p>La garde portait sur les seules créations. Un référentiel entièrement
   * déjà en banque n'en produisait aucune : le bouton restait gris, sans que
   * rien ne dise pourquoi, alors même que l'écran annonçait « 52 questions dans
   * le test ».</p>
   */
  readonly canCommit = computed(
    () => this.retained().length > 0 && !this.committing() && !this.needsProfessor(),
  );

  /* ── Redépôt du même référentiel ──────────────────────────────────── */

  /**
   * Toutes les questions retenues existent déjà dans la matière de dépôt.
   *
   * <p>C'est la signature d'un <b>redépôt du même document</b>, et l'API le
   * refuse. Un référentiel simplement enrichi ne remplit pas cette condition :
   * une seule ligne neuve suffit à le distinguer.</p>
   *
   * <p>Le seuil de deux écarte le fichier d'une seule ligne, où la
   * coïncidence ne prouve rien.</p>
   *
   * <p><b>Un titre de test lève la condition</b>, et c'est la limite qui
   * compte : déposer d'abord un référentiel en banque puis composer le test
   * depuis le même document est un usage normal — le second import apporte le
   * test. L'API ne refuse que le dépôt <i>à vide</i>, et l'écran ne doit pas
   * alerter là où rien ne sera refusé.</p>
   */
  readonly allAlreadyInBank = computed(() => {
    if (this.composesTest()) return false;

    const retenues = this.retained();
    return retenues.length >= 2 && retenues.every((q) => !!q.duplicateOfQuestionId);
  });

  /**
   * Passer outre le refus. Le défaut est de ne pas le faire : c'est un geste
   * explicite, et l'écran ne propose la case que lorsque la garde va
   * effectivement se déclencher.
   */
  readonly allowDuplicateImport = signal(false);

  constructor() {
    this.loadLearningPaths();
  }

  /* ── Chargement des référentiels ──────────────────────────────── */

  private loadLearningPaths(): void {
    this.learning.getLearningPaths(true, false).subscribe({
      next: (r) => this.learningPaths.set(r.data ?? []),
      error: () => this.learningPaths.set([]),
    });
  }

  professorLabel(p: Professor): string {
    return `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || p.email;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.file.set(input.files?.[0] ?? null);
  }

  /* ── Étape 1 → 2 ──────────────────────────────────────────────── */

  readonly canAnalyze = computed(
    () => !!this.file() && !!this.subjectId() && !this.analyzing(),
  );

  analyze(): void {
    const file = this.file();
    const subjectId = this.subjectId();
    if (!file || !subjectId) return;

    this.analyzing.set(true);
    this.uploadPercent.set(0);

    this.uploads.uploadWithProgress(file, UploadPurpose.QuestionImport).subscribe({
      next: (p) => {
        this.uploadPercent.set(p.percent);
        if (!p.done || !p.result) return;

        this.imports
          .analyze({
            objectKey: p.result.key,
            subjectId,
            language: this.language(),
            direction: this.direction(),
          })
          .pipe(finalize(() => this.analyzing.set(false)))
          .subscribe({
            next: (preview) => this.onAnalyzed(preview),
            error: () => this.fail('TEST_IMPORT.ANALYZE_ERROR'),
          });
      },
      error: () => {
        this.analyzing.set(false);
        this.fail('TEST_IMPORT.UPLOAD_ERROR');
      },
    });
  }

  private onAnalyzed(preview: TestImportPreview): void {
    this.preview.set(preview);
    this.mapping.set({ ...preview.suggestedMapping });
    this.uploadPercent.set(null);

    if (preview.rows.length === 0) {
      this.fail('TEST_IMPORT.NO_ROWS');
      return;
    }

    this.step.set('mapping');
  }

  /* ── Étape 2 ──────────────────────────────────────────────────── */

  roleOf(column: number): ImportColumnRole {
    return this.mapping()[column] ?? ImportColumnRole.Ignore;
  }

  /**
   * Change le rôle d'une colonne. Un rôle déjà pris ailleurs est libéré :
   * deux colonnes ne peuvent pas porter la même information, et laisser un
   * doublon s'installer produirait un import silencieusement faux.
   */
  setRole(column: number, role: ImportColumnRole): void {
    const next = { ...this.mapping() };

    if (role !== ImportColumnRole.Ignore) {
      for (const [key, value] of Object.entries(next)) {
        if (value === role && Number(key) !== column) {
          next[Number(key)] = ImportColumnRole.Ignore;
        }
      }
    }

    next[column] = role;
    this.mapping.set(next);
  }

  onRoleChange(column: number, value: string): void {
    this.setRole(column, Number(value) as ImportColumnRole);
  }

  cellOf(row: ImportedRow, column: number): string {
    return row.cells.find((c) => c.index === column)?.text ?? '';
  }

  /** Vrai si la cellule porte un surlignage — donc une bonne réponse. */
  hasHighlight(row: ImportedRow, column: number): boolean {
    return (row.cells.find((c) => c.index === column)?.highlightedLines.length ?? 0) > 0;
  }

  project(): void {
    const p = this.preview();
    if (!p || !this.mappingValid()) return;

    this.projecting.set(true);
    this.imports
      .preview(this.buildCommand(p))
      .pipe(finalize(() => this.projecting.set(false)))
      .subscribe({
        next: (previews) => {
          this.targetPreviews.set(previews);
          // La relecture porte sur la matière de dépôt : c'est là que
          // l'utilisateur corrige, et les corrections valent pour toutes.
          this.questions.set(previews[0]?.questions ?? []);
          // Une nouvelle projection repart du fichier : les corrections
          // portaient sur des lignes qui n'existent plus forcement.
          this.editedRows.set(new Set<number>());
          this.allowDuplicateImport.set(false);
          this.cancelEdit();
          this.step.set('review');
        },
        error: () => this.fail('TEST_IMPORT.PREVIEW_ERROR'),
      });
  }

  /* ── Étape 3 ──────────────────────────────────────────────────── */

  /** Désigne la bonne réponse quand l'extraction ne l'a pas trouvée. */
  markCorrect(question: ImportedQuestion, index: number): void {
    const updated = this.questions().map((q) => {
      if (q.rowNumber !== question.rowNumber) return q;
      return {
        ...q,
        answers: q.answers.map((a, i) => ({ ...a, isCorrect: i === index })),
      };
    });

    this.questions.set(updated);
    this.markEdited(question.rowNumber);
  }

  /* ── Édition d'une question ───────────────────────────────────── */

  /** Champs de référentiel proposés à la correction, pour le gabarit. */
  readonly draftFields = DRAFT_FIELDS;

  readonly titleMax = TITLE_MAX;

  isEditing(q: ImportedQuestion): boolean {
    return this.editingRow() === q.rowNumber;
  }

  isEdited(q: ImportedQuestion): boolean {
    return this.editedRows().has(q.rowNumber);
  }

  /**
   * Ouvre l'éditeur sur une copie profonde.
   *
   * <p>Copie <i>profonde</i> : les réponses sont des objets, et les modifier
   * en place toucherait la question d'origine — « Annuler » n'aurait alors
   * plus rien à restaurer.</p>
   */
  startEdit(q: ImportedQuestion): void {
    // `?? []` : l'API sérialise une liste vide en `null`, et l'étalement
    // échouerait au clic — le bouton de correction serait inerte pour une
    // question sans tag ni avertissement, c'est-à-dire la plupart.
    this.draft.set({
      ...q,
      answers: (q.answers ?? []).map((a) => ({ ...a })),
      tags: [...(q.tags ?? [])],
      warnings: [...(q.warnings ?? [])],
    });
    this.editingRow.set(q.rowNumber);
  }

  cancelEdit(): void {
    this.editingRow.set(null);
    this.draft.set(null);
  }

  /** Applique une correction à la copie de travail, sans toucher l'original. */
  private patchDraft(patch: Partial<ImportedQuestion>): void {
    const d = this.draft();
    if (d) this.draft.set({ ...d, ...patch });
  }

  setDraftTitle(title: string): void {
    this.patchDraft({ title });
  }

  fieldValue(name: DraftFieldDef['name']): string {
    return this.draft()?.[name] ?? '';
  }

  setDraftField(name: DraftFieldDef['name'], value: string): void {
    this.patchDraft({ [name]: value } as Partial<ImportedQuestion>);
  }

  setDraftAnswer(index: number, content: string): void {
    const d = this.draft();
    if (!d) return;
    this.patchDraft({
      answers: d.answers.map((a, i) => (i === index ? { ...a, content } : a)),
    });
  }

  setDraftCorrect(index: number): void {
    const d = this.draft();
    if (!d) return;
    this.patchDraft({
      answers: d.answers.map((a, i) => ({ ...a, isCorrect: i === index })),
    });
  }

  addDraftAnswer(): void {
    const d = this.draft();
    if (!d) return;
    this.patchDraft({ answers: [...d.answers, { content: '', isCorrect: false }] });
  }

  removeDraftAnswer(index: number): void {
    const d = this.draft();
    if (!d) return;
    this.patchDraft({ answers: d.answers.filter((_, i) => i !== index) });
  }

  /**
   * Range la copie de travail à la place de l'original.
   *
   * <p><b>Les marques de doublon sont effacées quand le contenu a changé.</b>
   * C'est exactement ce que le serveur fait de son côté : il recalcule
   * l'empreinte et refait sa détection. Les garder afficherait « déjà en
   * banque » sur une question que la correction vient de rendre neuve, et le
   * compte annoncé ne serait pas celui que l'import produit.</p>
   *
   * <p>Une option laissée vide est retirée : le professeur qui ajoute une
   * ligne puis se ravise ne crée pas une réponse sans texte.</p>
   */
  saveEdit(): void {
    const d = this.draft();
    if (!d || !this.draftValid()) return;

    const clean: ImportedQuestion = {
      ...d,
      title: d.title.trim(),
      answers: d.answers
        .map((a) => ({ ...a, content: a.content.trim() }))
        .filter((a) => a.content.length > 0),
      theme: trimmedOrNull(d.theme),
      level: trimmedOrNull(d.level),
      skill: trimmedOrNull(d.skill),
      targeted: trimmedOrNull(d.targeted),
      prerequisites: trimmedOrNull(d.prerequisites),
    };

    const original = this.questions().find((q) => q.rowNumber === d.rowNumber);

    if (original && !sameContent(original, clean)) {
      clean.duplicateOfQuestionId = null;
      clean.repeatOfRowNumber = null;
    }

    this.questions.set(
      this.questions().map((q) => (q.rowNumber === clean.rowNumber ? clean : q)),
    );
    this.markEdited(clean.rowNumber);
    this.cancelEdit();
  }

  private markEdited(rowNumber: number): void {
    this.editedRows.set(new Set([...this.editedRows(), rowNumber]));
  }

  commit(): void {
    const p = this.preview();
    if (!p) return;

    if (this.needsProfessor()) {
      this.fail('TEST_IMPORT.PROFESSOR_REQUIRED');
      return;
    }

    // Un titre saisi engage la composition d'un test : ses réglages doivent
    // tenir. Sans titre, le formulaire est invalide par construction (titre
    // obligatoire) et c'est sans conséquence — rien ne sera composé.
    if (this.composesTest() && this.testForm?.invalid) {
      this.testForm.markAllAsTouched();
      this.fail('TEST_IMPORT.SETTINGS_INVALID');
      return;
    }

    this.swal.confirm(
      this.translate.instant('TEST_IMPORT.CONFIRM_COMMIT', {
        count: this.retained().length,
      }),
      () => this.doCommit(p),
    );
  }

  private doCommit(p: TestImportPreview): void {
    this.committing.set(true);

    const command = this.buildCommand(p);

    // Ce que l'écran a corrigé part avec le commit — et pas avant. `preview`
    // doit montrer le fichier ; seul le commit honore les corrections.
    //
    // Le bloc du dépôt est le seul éditable (`isEditableBlock`) : le fichier
    // est le même pour toutes les cibles, et les corrections se font une fois.
    // C'est donc `questions()` qu'on envoie, et le serveur l'applique à chaque
    // matière visée en y refaisant sa propre détection de doublons.
    command.questions = this.questions();

    // L'API refuse un redépôt du même document ; ce drapeau lève le refus.
    // Il ne part que lorsque la case a été cochée, et la case ne s'affiche
    // que lorsque la garde va se déclencher.
    if (this.allowDuplicateImport()) command.allowDuplicateImport = true;

    const v = (this.testForm?.value ?? {}) as Record<string, any>;
    command.testTitle = String(v['title'] ?? '').trim() || null;

    // Sans titre, aucun test n'est composé : envoyer des réglages n'aurait
    // rien à paramétrer.
    if (command.testTitle) {
      command.description = String(v['description'] ?? '').trim() || null;
      command.timeLimitMinutes = Number(v['timeLimitMinutes']);
      command.gradingScale = Number(v['gradingScale']) || null;
      command.passingPercentage = Number(v['passingPercentage']);
      command.maxAttempts = Number(v['maxAttempts']);
      command.showResultsImmediately = !!v['showResultsImmediately'];
      command.allowReview = !!v['allowReview'];
    }

    this.imports
      .commit(command)
      .pipe(finalize(() => this.committing.set(false)))
      .subscribe({
        next: (result) => {
          this.result.set(result);
          this.step.set('done');
        },
        error: (e) => this.failFrom(e, 'TEST_IMPORT.COMMIT_ERROR'),
      });
  }

  /* ── Navigation ───────────────────────────────────────────────── */

  back(): void {
    if (this.step() === 'review') this.step.set('mapping');
    else if (this.step() === 'mapping') this.step.set('source');
  }

  goToBank(): void {
    const prefix = this.router.url.startsWith('/admin') ? '/admin' : '/teacher';
    this.router.navigate([prefix, 'question-bank']);
  }

  // Le parcours, la matière et le professeur désigné survivent : on enchaîne
  // le plus souvent sur un second fichier de la même matière.
  restart(): void {
    this.file.set(null);
    this.preview.set(null);
    this.mapping.set({});
    this.questions.set([]);
    this.targetPreviews.set([]);
    this.editedRows.set(new Set<number>());
    this.allowDuplicateImport.set(false);
    this.cancelEdit();
    this.result.set(null);
    this.testForm = null;
    this.testInfo.set(null);
    this.step.set('source');
  }

  /**
   * Récupère le formulaire de test à son ouverture et suit ses valeurs.
   *
   * <p>Le suivi sert à deux choses : savoir si un titre est saisi — donc si un
   * test sera composé — et retrouver la saisie après un aller-retour vers
   * l'étape du mapping, qui détruit le formulaire.</p>
   */
  onTestFormReady(form: FormGroup): void {
    this.formWatch?.unsubscribe();
    this.testForm = form;

    // Rien n'est posé ici : `formReady` est émis pendant la détection de
    // changements, et écrire le signal à cet instant relirait des expressions
    // déjà évaluées. Les valeurs arrivent à la première frappe, ce qui suffit —
    // le formulaire s'ouvre de toute façon avec un titre vide.
    this.formWatch = form.valueChanges.subscribe((v) => this.testInfo.set(v));
  }

  /* ── Utilitaires ──────────────────────────────────────────────── */

  /* ── Parcours supplémentaires ─────────────────────────────────── */

  /**
   * Parcours proposables : tous ceux qui ne sont pas déjà retenus.
   *
   * <p>Ceux du même cycle viennent en tête — c'est le cas courant, un même
   * programme se rejouant d'une année à l'autre du même niveau. Les autres
   * restent atteignables : rien n'interdit de verser un référentiel ailleurs,
   * mais l'écran le signale plutôt que de le taire.</p>
   */
  readonly availableTargetPaths = computed(() => {
    const level = this.sourceLevel();
    const taken = new Set(this.chosenTargets().map((t) => t.pathId));

    return this.learningPaths()
      .filter((p) => !taken.has(p.id))
      .sort((a, b) => {
        const sameA = a.level === level ? 0 : 1;
        const sameB = b.level === level ? 0 : 1;
        return sameA - sameB || (a.title ?? '').localeCompare(b.title ?? '');
      });
  });

  /** Cycle de la première cible, pour signaler celles qui n'en relèvent pas. */
  readonly sourceLevel = computed(() => this.depositTarget()?.pathLevel ?? null);

  /**
   * Vrai quand la cible ne relève pas du cycle de la première retenue.
   * Signalé, jamais refusé : rien n'interdit de verser un référentiel ailleurs.
   */
  isOffCycle(t: ImportTarget): boolean {
    const level = this.sourceLevel();
    return !!level && !!t.pathLevel && t.pathLevel !== level;
  }

  readonly canAddTarget = computed(
    () => this.targetPathId() !== null && this.targetSubjectId() !== null,
  );

  onTargetPathChange(id: number | null): void {
    this.targetPathId.set(id);
    this.onTargetSubjectChange(null);
    this.targetSubjects.set([]);
    if (!id) return;

    this.learning.getSubjectsByLearningPath(id, false).subscribe({
      next: (r) => {
        // Le parcours a pu changer pendant la requête : ses matières ne sont
        // plus celles du parcours affiché.
        if (this.targetPathId() === id) this.targetSubjects.set(r?.data ?? []);
      },
      error: () => {
        if (this.targetPathId() === id) this.targetSubjects.set([]);
      },
    });
  }

  /**
   * Change de matière visée et recharge ses professeurs.
   *
   * <p>Le titulaire précédent est oublié : garder celui d'une autre matière
   * attribuerait le test à quelqu'un qui n'y enseigne pas.</p>
   */
  onTargetSubjectChange(id: number | null): void {
    this.targetSubjectId.set(id);
    this.targetProfessorId.set(null);
    this.targetProfessors.set([]);

    if (!id || !this.isAdmin) return;

    this.professorsApi.getProfessors(id).subscribe({
      next: (r) => {
        // Réponse d'une matière quittée entre-temps : la garder désignerait
        // d'office un professeur qui n'enseigne pas celle qui est affichée.
        if (this.targetSubjectId() !== id) return;
        const list = r?.data ?? [];
        this.targetProfessors.set(list);

        // Un seul professeur sur la matière : le choix n'en est pas un.
        if (list.length === 1) this.targetProfessorId.set(Number(list[0].id));
      },
      error: () => {
        if (this.targetSubjectId() === id) this.targetProfessors.set([]);
      },
    });
  }

  addTarget(): void {
    const pathId = this.targetPathId();
    const subjectId = this.targetSubjectId();
    if (pathId === null || subjectId === null) return;

    // La même matière deux fois n'écrirait pas deux fois — le serveur
    // dédoublonne les cibles — mais la voir listée en double laisserait croire
    // le contraire.
    if (this.chosenTargets().some((t) => t.subjectId === subjectId)) return;

    const path = this.learningPaths().find((p) => p.id === pathId);
    const subject = this.targetSubjects().find((s) => s.id === subjectId);
    const professorId = this.targetProfessorId();
    const professor = this.targetProfessors().find((p) => Number(p.id) === professorId);

    this.chosenTargets.update((list) => [
      ...list,
      {
        pathId,
        pathTitle: path?.title ?? `#${pathId}`,
        subjectId,
        subjectName: subject?.name ?? `#${subjectId}`,
        professorId,
        professorName: professor ? this.professorLabel(professor) : null,
        pathLevel: path?.level ?? null,
      },
    ]);

    this.targetPathId.set(null);
    this.onTargetSubjectChange(null);
    this.targetSubjects.set([]);
  }

  removeTarget(subjectId: number): void {
    this.chosenTargets.update((list) => list.filter((t) => t.subjectId !== subjectId));
  }
  /* ── Relecture par test ───────────────────────────────────────── */

  /**
   * Blocs dépliés, par position.
   *
   * <p>Celui du dépôt est ouvert d'emblée : c'est là que la relecture se fait
   * et que les corrections s'apportent. Les autres reçoivent le même contenu —
   * c'est le même fichier — et ne diffèrent que par ce qui s'y trouve déjà ;
   * les replier évite de faire relire trois fois les mêmes énoncés.</p>
   */
  readonly expandedTargets = signal<Set<number>>(new Set([0]));

  isTargetExpanded(index: number): boolean {
    return this.expandedTargets().has(index);
  }

  toggleTargetBlock(index: number): void {
    const next = new Set(this.expandedTargets());
    next.has(index) ? next.delete(index) : next.add(index);
    this.expandedTargets.set(next);
  }

  /**
   * Les questions d'un bloc.
   *
   * <p>Le premier rend celles que l'utilisateur relit et corrige ; les autres,
   * la projection propre à leur matière — dont les doublons diffèrent.</p>
   */
  /**
   * Les autres cibles, avec le contenu corrigé et leur propre statut.
   *
   * <p>Le fichier est le même pour toutes, et le serveur applique partout les
   * corrections faites sur la matière de dépôt. Laisser ces blocs afficher le
   * texte d'origine ferait relire au professeur un énoncé que l'import
   * n'écrira pas — l'écran annoncerait une chose, la base en recevrait une
   * autre.</p>
   *
   * <p>Le statut de doublon, lui, reste celui du bloc : il se juge matière par
   * matière, et c'est le serveur qui le refera.</p>
   */
  private readonly editedTargets = computed<ImportedQuestion[][]>(() => {
    const corrected = new Map(this.questions().map((q) => [q.rowNumber, q]));

    return this.targetPreviews().map((target) =>
      target.questions.map((q) => {
        const fixed = corrected.get(q.rowNumber);
        return fixed
          ? {
              ...fixed,
              duplicateOfQuestionId: q.duplicateOfQuestionId,
              repeatOfRowNumber: q.repeatOfRowNumber,
            }
          : q;
      }),
    );
  });

  questionsOf(index: number): ImportedQuestion[] {
    return index === 0 ? this.questions() : (this.editedTargets()[index] ?? []);
  }

  /** Ce qu'un bloc créerait réellement, une fois ses doublons écartés. */
  createdCountOf(index: number): number {
    return this.questionsOf(index).filter(
      (q) => !q.duplicateOfQuestionId && q.answers.length >= 2,
    ).length;
  }

  duplicateCountOf(index: number): number {
    return this.questionsOf(index).filter((q) => !!q.duplicateOfQuestionId).length;
  }

  /** Ce qu'un bloc posera dans son test : le neuf exploitable, et l'existant. */
  retainedCountOf(index: number): number {
    return this.questionsOf(index).filter(isRetained).length;
  }

  /** Lignes que le fichier répète, et qui ne seront donc écrites qu'une fois. */
  repeatCountOf(index: number): number {
    return this.questionsOf(index).filter((q) => !!q.repeatOfRowNumber).length;
  }

  /**
   * Vrai pour le seul bloc du dépôt.
   *
   * <p>Les corrections s'y font une fois et valent pour toutes les cibles : le
   * fichier est le même. Les rendre modifiables ailleurs laisserait croire que
   * chaque parcours se corrige séparément.</p>
   */
  isEditableBlock(index: number): boolean {
    return index === 0;
  }

  private buildCommand(p: TestImportPreview): TestImportCommit {
    return {
      objectKey: p.objectKey,
      subjectId: this.subjectId()!,
      language: this.language(),
      direction: this.direction(),
      // Le format décide du sort des sauts de ligne de l'énoncé : gardés pour
      // un Word, où chaque ligne est voulue ; remis sur une ligne pour un PDF.
      format: p.format,
      mapping: this.mapping(),
      rows: p.rows,
      // Sans effet pour un professeur : l'API retient toujours l'identité du
      // jeton quand elle en porte une.
      professorId: this.professorId(),

      // Le même référentiel, écrit dans chaque parcours retenu — chacun avec
      // son propre titulaire.
      additionalTargets: this.chosenTargets().map((t) => ({
        subjectId: t.subjectId,
        professorId: t.professorId,
      })),
    };
  }

  private fail(key: string): void {
    this.swal.error(this.translate.instant(key));
  }

  /**
   * Erreur d'appel : on préfère le message du serveur quand il y en a un. Les
   * refus d'import sont explicites — professeur manquant, colonne question non
   * désignée — et les remplacer par un « échec de l'import » générique
   * priverait l'utilisateur de ce qu'il doit corriger.
   */
  private failFrom(error: unknown, fallbackKey: string): void {
    const message = (error as { error?: { message?: string } })?.error?.message;
    this.swal.error(message || this.translate.instant(fallbackKey));
  }
}
